import type { ServicePageConfig } from "../../../types";
import {
  getPackagePricingRows,
  PACKAGE_PRICE_HINT_SV,
  PACKAGE_PRICE_HINT_EN,
} from "../../../package-pricing";
import { getCityMunicipalityList } from "../../../cities";

const stockholmKommunerText = getCityMunicipalityList("stockholm");

export const flytthjalpTabyPage: ServicePageConfig = {
  slug: "flytthjalp-taby",
  serviceCategory: "flytt",
  city: "stockholm",

  meta: {
    title: "Flytthjälp Täby – Villa, Radhus & Lägenhet | Ungdomskraft",
    description:
      "Flytthjälp i Täby: Näsbypark, Grindtorp, Täby Park, Viggbyholm, Gribbylund, Ensta, Hägernäs och Arninge. Skåpbil och bärare, fast pris, RUT-avdrag.",
    h1: "Flytthjälp Täby",
  },

  hero: {
    heading: "Flytthjälp i Täby",
    intro:
      "Ska villan i Viggbyholm tömmas eller ska du flytta in i en ny lägenhet i Täby Park? Ungdomskraft kommer med skåpbil och bärare till hela Täby – Näsbypark, Grindtorp, Gribbylund, Ensta, Hägernäs, Roslags-Näsby och Arninge. Vi bär, kör och ställer allt på plats, och du ser priset innan du bokar.",
    heroImage: "/hero-flytthjalp.png",
    heroImageAlt: "Ungdomskraft bär möbler från en villa vid flytthjälp i Täby",
  },

  featureCards: {
    sinceText: "Flytthjälp sedan 2014",
    storyText:
      "Sedan 2014 har Ungdomskraft flyttat hem i norrort. I Täby är det ofta villor med källare, vind och garage som ska tömmas, och våra bärare vet hur man planerar en stor husflytt så att allt kommer med och inget går sönder på vägen.",
    bookYourText: "Boka din flytt i Täby",
    bookDescText:
      "Ange adresserna, beskriv bohaget och välj datum. Priset visas direkt – bärare och skåpbil ingår.",
    vehicleTitleText: "Bärare och skåpbil i samma bokning",
    vehicleDescText:
      "Du behöver inte ordna släp eller hyrbil. Vi kör skåpbilen, lastar den säkert och tar fler vändor om huset är stort.",
  },

  itemsSection: {
    heading: "Vanligt bohag vid flytt i Täby",
    items: [
      {
        name: "Soffor & fåtöljer",
        description: "Stora soffgrupper och fåtöljer från vardagsrum och tv-rum",
        image: "/sofa.webp",
        alt: "Soffgrupp som bärs ut vid villaflytt i Näsbypark, Täby",
      },
      {
        name: "Sängar & madrasser",
        description: "Kontinentalsängar och barnsängar från flera sovrum",
        image: "/bed.webp",
        alt: "Kontinentalsäng som flyttas vid flytthjälp i Täby",
      },
      {
        name: "Bord & stolar",
        description: "Matsalsbord, utemöbler och hemmakontor",
        image: "/table.webp",
        alt: "Matsalsbord vid flytt från villa i Viggbyholm, Täby",
      },
      {
        name: "Vitvaror",
        description: "Tvättmaskin, torktumlare, frysbox och extra kylskåp",
        image: "/washer.webp",
        alt: "Frysbox och tvättmaskin vid flytt i Gribbylund, Täby",
      },
      {
        name: "Garderober & byråer",
        description: "Garderobssystem, byråer och bokhyllor",
        image: "/wardrobe.webp",
        alt: "Garderob som tas isär vid flytt i Grindtorp, Täby",
      },
      {
        name: "Flyttkartonger",
        description: "Kartonger och förrådslådor från vind, källare och garage",
        image: "/box.webp",
        alt: "Flyttkartonger vid flytt till Täby Park",
      },
    ],
  },

  seoContent: {
    mainHeading: "Flytthjälp i Täby – villaflytt, radhusflytt och lägenhetsflytt",
    mainText:
      "Täby är i första hand en villakommun, och det präglar de flesta flyttar vi gör här. I Viggbyholm, Gribbylund, Ensta, Skarpäng och Täby kyrkby står villor på stora tomter, ofta med källare, vind, garage och förråd som alla ska tömmas samma dag. En sådan flytt handlar om att planera: hur många bärare som behövs, hur mycket som ryms per vända och i vilken ordning rummen ska tas så att det tyngsta hamnar längst in i bilen. Många villaflyttar i Täby är också generationsskiften, när ett hus som familjen bott i länge säljs och bohaget ska delas upp mellan flera adresser.\n\nLägenheterna i Täby finns framför allt kring Täby centrum och längs Roslagsbanan. Grindtorp och Näsbydal är flerbostadsområden från 1960- och 70-talen, i Näsbypark finns höghus bredvid villorna, och på det gamla galoppfältet växer Täby Park fram med nya kvarter. I Hägernäs har tidigare militärmark blivit bostäder vid vattnet, och kring Arninge byggs det både handel och nya hem. I nyproduktionen går lastning ofta via garage eller en gemensam lastplats, medan de äldre husen kring centrum har hissar i olika storlekar.\n\nE18 går genom Täby och Norrortsleden leder västerut mot E4, så vi når både Stockholms innerstad och resten av norrort snabbt. Roslagsbanan med stationer som Roslags-Näsby, Täby centrum och Viggbyholm gör kommunen populär bland familjer som pendlar in till stan. Vanliga flyttar är lägenhet i Stockholm till radhus i Täby, villa i Täby till lägenhet i Täby centrum och ungdomar som flyttar hemifrån till en första etta. Vi anpassar teamet efter huset och kör flera vändor när det behövs.",
    preparationHeading: "Så förbereder du en flytt i Täby",
    preparationText:
      "Gå igenom vind, källare och garage först. Det är där det gömmer sig mest, och det är lättare att bestämma vad som ska med innan flyttdagen. Det som inte ska med kan vi köra bort i en separat bortforsling. Packa i kartonger av samma storlek så att de staplas stabilt i bilen, och märk dem med rum.\n\nSe till att uppfarten är tom så att skåpbilen kan backa nära entrén, och öppna garageporten innan vi kommer. Flyttar du till en lägenhet i Täby Park eller kring Täby centrum, ta reda på var lastplatsen finns och om hissen behöver bokas. Frosta av frysbox och kyl dygnet innan och töm tvättmaskinen på vatten.",
    additionalSections: [
      {
        heading: "Kommundelar i Täby där vi flyttar",
        content:
          "Vi flyttar i hela Täby kommun. I villaområdena Viggbyholm, Gribbylund, Ensta, Skarpäng, Ella Gård och Täby kyrkby tar vi hela hus med förråd och garage. I Näsbypark blandas villor och höghus.\n\nKring Täby centrum flyttar vi i Tibble, Grindtorp och Näsbydal, och vi flyttar in och ut ur nyproduktionen i Täby Park, Hägernäs och Arninge. Roslags-Näsby och de andra områdena längs Roslagsbanan täcker vi lika enkelt. Körsträcka inom 20 km ingår, vilket räcker för de flesta flyttar mellan Täby och Stockholm.",
        cta: { text: "Se flytthjälp i grannkommunen Åkersberga →", href: "/flytthjalp-akersberga" },
      },
      {
        heading: "Stor villaflytt i Täby",
        content:
          "En villa med fyra sovrum, källare och garage är en helt annan flytt än en tvåa. Vi bokar in paket efter mängd och tid – Extra Stor för upp till 4 timmar och Mega Stor för upp till 8 timmar – och sätter ihop teamet så att hela huset hinns med. Behöver bohaget delas mellan flera adresser, till exempel när barnen tar över möbler, kan vi köra till flera ställen under samma dag.\n\nFlytthjälp ger rätt till RUT-avdrag: 50 % av arbetskostnaden, upp till 75 000 kr per person och år. Vi drar av avdraget direkt på fakturan.",
        cta: { text: "Se bortforsling i Täby →", href: "/bortforsling-taby" },
      },
      {
        heading: "Varför boka flytthjälp i Täby med Ungdomskraft?",
        listItems: [
          {
            label: "Vana vid villaflyttar –",
            text: "Vi har tömt många villor i norrort och vet hur man planerar vind, källare och garage i rätt ordning.",
          },
          {
            label: "Nära E18 –",
            text: "Vi har bilar och personal i hela regionen och tar oss snabbt till Täby, ofta samma vecka.",
          },
          {
            label: "Priset syns direkt –",
            text: "Du ser totalpriset innan du bokar. Inga offerter och inga tillägg för trappor.",
          },
          {
            label: "Försäkrad flytt –",
            text: "Bohag, hus och trapphus är försäkrade genom Trygg Hansa under hela flytten.",
          },
          {
            label: "Allt ingår –",
            text: "Skåpbil, bärare, filtar och spännband. Du behöver inte hyra eller köpa något.",
          },
        ],
      },
    ],
  },

  faq: [
    {
      question: "Vad kostar flytthjälp i Täby?",
      answer:
        "Några möbler bokas som Liten (995 kr) eller Mellan (1 495 kr). En fullt möblerad lägenhet landar ofta på 2 495–4 995 kr, och en hel villa bokas som Mega Stor (8 995 kr, upp till 8 timmar). Körning inom 20 km ingår och priset visas innan du bokar.",
    },
    {
      question: "Får jag RUT-avdrag på flytthjälp i Täby?",
      answer:
        "Ja, 50 % av arbetskostnaden, upp till 75 000 kr per person och år. Vi drar av det direkt på fakturan.",
    },
    {
      question: "Hur snabbt kan ni hjälpa mig att flytta i Täby?",
      answer:
        "Vi har ofta lediga tider samma vecka och ibland samma dag. Du ser tiderna direkt i bokningen.",
    },
    {
      question: "Kan ni flytta en hel villa med källare och garage?",
      answer:
        "Ja. Vi tar vind, källare, garage och trädgårdsmöbler. Stora hus bokas som Extra Stor eller Mega Stor, och vi gör fler vändor om det behövs.",
    },
    {
      question: "Kan bohaget köras till flera adresser samma dag?",
      answer:
        "Ja. Lägg till adresserna i bokningen så planerar vi körningen, till exempel när möbler ska till både nya bostaden och till barnen.",
    },
    {
      question: "Flyttar ni in i nyproduktion i Täby Park?",
      answer:
        "Ja. Berätta i bokningen hur lastningen går till och om det finns en inflyttningstid att passa.",
    },
    {
      question: "Bär ni i trappor utan hiss?",
      answer: "Ja, i både lägenhetshus och villor med flera plan. Trappor kostar inget extra.",
    },
    {
      question: "Demonterar ni möbler?",
      answer:
        "Ja, sängar, garderober och bord tas isär när det behövs och monteras ihop igen på nya adressen.",
    },
    {
      question: "Flyttar ni frysbox och tvättmaskin?",
      answer:
        "Ja, alla vanliga vitvaror. Frosta av frysen dygnet innan och töm tvättmaskinen på vatten.",
    },
    {
      question: "Kan ni flytta på helger?",
      answer: "Ja, vi flyttar alla dagar i veckan.",
    },
    {
      question: "Kan ni ta bort det som inte ska med?",
      answer:
        "Ja, boka en bortforsling samma dag så kör vi det till återvinning eller återbruk.",
    },
    {
      question: "Vilka delar av Täby täcker ni?",
      answer:
        "Hela Täby: Täby centrum, Tibble, Grindtorp, Näsbydal, Näsbypark, Roslags-Näsby, Viggbyholm, Gribbylund, Ensta, Skarpäng, Ella Gård, Täby kyrkby, Hägernäs, Arninge och Täby Park.",
    },
    {
      question: "Vilka andra kommuner flyttar ni i?",
      answer: `Vi utför flytthjälp i hela Stockholmsregionen: ${stockholmKommunerText}. Körsträcka inom 20 km ingår alltid.`,
    },
  ],

  priceHint: PACKAGE_PRICE_HINT_SV.transport,

  pricingRows: getPackagePricingRows("transport", "sv"),

  dynamicHeadings: {
    threeSteps: "Flytthjälp i Täby i 3 steg",
    step2Text:
      "Vi kommer med skåpbil och bärare, tömmer rum för rum och lastar tungt längst in. Sedan kör vi till nya adressen – en eller flera.",
    step3Title: "Allt inburet",
    step3Desc:
      "Vi bär in och ställer möblerna i rätt rum, och monterar ihop det som togs isär. Du kan börja packa upp direkt.",
    ctaHeading: "Boka flytthjälp i Täby idag",
    ctaText:
      "Ange adresser, välj paket och tid. Priset visas direkt och RUT-avdraget dras på fakturan.",
  },

  en: {
    slug: "moving-help-taby",
    meta: {
      title: "Moving Help Täby – Houses, Terraces & Flats | Ungdomskraft",
      description:
        "Moving help in Täby: Näsbypark, Grindtorp, Täby Park, Viggbyholm, Gribbylund, Ensta, Hägernäs and Arninge. Van and movers, fixed price, RUT deduction.",
      h1: "Moving Help Täby",
    },
    hero: {
      heading: "Moving Help in Täby",
      intro:
        "Clearing the house in Viggbyholm or moving into a new flat in Täby Park? Ungdomskraft brings a van and movers anywhere in Täby — Näsbypark, Grindtorp, Gribbylund, Ensta, Hägernäs, Roslags-Näsby and Arninge. We carry, drive and put everything in place, and you see the price before you book.",
      heroImageAlt: "Ungdomskraft carrying furniture from a house during moving help in Täby",
    },
    featureCards: {
      sinceText: "Moving help since 2014",
      storyText:
        "Since 2014 Ungdomskraft has moved homes in Stockholm's northern suburbs. In Täby it's often houses with a basement, loft and garage to clear, and our movers know how to plan a big house move so everything comes along and nothing breaks on the way.",
      bookYourText: "Book your Täby move",
      bookDescText:
        "Enter the addresses, describe your household and pick a date. The price shows instantly — movers and van included.",
      vehicleTitleText: "Movers and van in one booking",
      vehicleDescText:
        "No trailer or rental van to arrange. We drive the van, load it safely and make extra trips if the house is big.",
    },
    itemsSection: {
      heading: "Typical household items on Täby moves",
      items: [
        {
          name: "Sofas & armchairs",
          description: "Large sofa sets and armchairs from living and TV rooms",
          image: "/sofa.webp",
          alt: "Sofa set carried out during a house move in Näsbypark, Täby",
        },
        {
          name: "Beds & mattresses",
          description: "Continental beds and children's beds from several bedrooms",
          image: "/bed.webp",
          alt: "Continental bed moved during moving help in Täby",
        },
        {
          name: "Tables & chairs",
          description: "Dining tables, garden furniture and home offices",
          image: "/table.webp",
          alt: "Dining table during a house move in Viggbyholm, Täby",
        },
        {
          name: "Appliances",
          description: "Washing machine, dryer, chest freezer and spare fridge",
          image: "/washer.webp",
          alt: "Chest freezer and washing machine during a move in Gribbylund, Täby",
        },
        {
          name: "Wardrobes & dressers",
          description: "Wardrobe systems, dressers and bookcases",
          image: "/wardrobe.webp",
          alt: "Wardrobe taken apart during a move in Grindtorp, Täby",
        },
        {
          name: "Moving boxes",
          description: "Boxes and storage crates from loft, basement and garage",
          image: "/box.webp",
          alt: "Moving boxes during a move to Täby Park",
        },
      ],
    },
    seoContent: {
      mainHeading: "Moving help in Täby — house moves, terraced-house moves and flat moves",
      mainText:
        "Täby is first and foremost a municipality of detached houses, and that shapes most of the moves we do here. In Viggbyholm, Gribbylund, Ensta, Skarpäng and Täby kyrkby houses stand on large plots, often with a basement, loft, garage and storage that all need clearing on the same day. A move like that is about planning: how many movers are needed, how much fits per trip and in which order to take the rooms so the heaviest things go furthest into the van. Many house moves in Täby are also generational, when a house the family has lived in for years is sold and the contents are split between several addresses.\n\nTäby's flats are mainly around Täby centrum and along the Roslagsbanan railway. Grindtorp and Näsbydal are apartment areas from the 1960s and 70s, Näsbypark has tower blocks next to the houses, and on the old racecourse Täby Park is growing with new blocks. In Hägernäs former military land has become homes by the water, and around Arninge both retail and new housing are being built. In new builds loading often goes via a garage or shared loading spot, while the older buildings around the centre have lifts of varying size.\n\nThe E18 runs through Täby and Norrortsleden leads west towards the E4, so we reach both central Stockholm and the rest of the northern suburbs quickly. The Roslagsbanan line, with stations such as Roslags-Näsby, Täby centrum and Viggbyholm, makes the municipality popular with families commuting into town. Typical moves are a Stockholm flat to a terraced house in Täby, a Täby house to a flat at Täby centrum and young people leaving home for a first studio. We size the team to the house and make several trips when needed.",
      preparationHeading: "How to prepare a move in Täby",
      preparationText:
        "Go through the loft, basement and garage first. That's where most things hide, and it's easier to decide what's coming before moving day. Anything that isn't coming can go in a separate junk removal. Pack in boxes of the same size so they stack steadily in the van, and label them by room.\n\nMake sure the driveway is clear so the van can reverse close to the entrance, and open the garage door before we arrive. Moving to a flat in Täby Park or around Täby centrum? Find out where the loading spot is and whether the lift needs booking. Defrost the chest freezer and fridge the day before and drain the washing machine.",
      additionalSections: [
        {
          heading: "Districts in Täby where we move",
          content:
            "We move across the whole of Täby municipality. In the house areas of Viggbyholm, Gribbylund, Ensta, Skarpäng, Ella Gård and Täby kyrkby we take whole houses with storage and garage. Näsbypark mixes houses and tower blocks.\n\nAround Täby centrum we move in Tibble, Grindtorp and Näsbydal, and we move in and out of the new builds in Täby Park, Hägernäs and Arninge. Roslags-Näsby and the other areas along the Roslagsbanan line are just as easy. Driving within 20 km is included, which covers most moves between Täby and Stockholm.",
          cta: { text: "See moving help in neighbouring Åkersberga →", href: "/en/moving-help-akersberga" },
        },
        {
          heading: "Large house moves in Täby",
          content:
            "A four-bedroom house with basement and garage is a completely different move from a one-bed flat. We book packages by volume and time — Extra Large for up to 4 hours and Mega Large for up to 8 hours — and build the team so the whole house gets done. If the contents are split between several addresses, for example when the children take over furniture, we can drive to several places on the same day.\n\nMoving help qualifies for the RUT tax deduction: 50% of the labour cost, up to SEK 75,000 per person per year. We apply the deduction directly on the invoice.",
          cta: { text: "See junk removal in Täby →", href: "/en/junk-removal-taby" },
        },
        {
          heading: "Why book moving help in Täby with Ungdomskraft?",
          listItems: [
            {
              label: "Used to house moves –",
              text: "We've cleared many houses in the northern suburbs and know how to plan loft, basement and garage in the right order.",
            },
            {
              label: "Close to the E18 –",
              text: "We have vans and staff across the region and get to Täby quickly, often the same week.",
            },
            {
              label: "Price shown straight away –",
              text: "You see the total before you book. No quotes and no stair surcharge.",
            },
            {
              label: "Insured move –",
              text: "Belongings, house and stairwell are insured through Trygg Hansa throughout the move.",
            },
            {
              label: "Everything included –",
              text: "Van, movers, blankets and tie-downs. You don't need to rent or buy anything.",
            },
          ],
        },
      ],
    },
    faq: [
      {
        question: "How much does moving help in Täby cost?",
        answer:
          "A few pieces are booked as Small (SEK 995) or Medium (SEK 1,495). A fully furnished flat often lands at SEK 2,495–4,995, and a whole house is booked as Mega Large (SEK 8,995, up to 8 hours). Driving within 20 km is included and the price shows before you book.",
      },
      {
        question: "Do I get the RUT deduction on moving help in Täby?",
        answer:
          "Yes, 50% of the labour cost, up to SEK 75,000 per person per year. We deduct it directly on the invoice.",
      },
      {
        question: "How quickly can you help me move in Täby?",
        answer:
          "We often have slots the same week and sometimes the same day. You see the times directly in the booking.",
      },
      {
        question: "Can you move a whole house with basement and garage?",
        answer:
          "Yes. We take the loft, basement, garage and garden furniture. Large houses are booked as Extra Large or Mega Large, and we make more trips if needed.",
      },
      {
        question: "Can the contents go to several addresses on the same day?",
        answer:
          "Yes. Add the addresses in the booking and we'll plan the driving, for example when furniture is going to both the new home and the children.",
      },
      {
        question: "Do you move people into new builds in Täby Park?",
        answer:
          "Yes. Tell us in the booking how loading works and whether there's a move-in time to meet.",
      },
      {
        question: "Do you carry on stairs without a lift?",
        answer: "Yes, in both apartment blocks and multi-storey houses. Stairs cost nothing extra.",
      },
      {
        question: "Do you dismantle furniture?",
        answer:
          "Yes, beds, wardrobes and tables are taken apart when needed and reassembled at the new address.",
      },
      {
        question: "Do you move chest freezers and washing machines?",
        answer:
          "Yes, all common appliances. Defrost the freezer the day before and drain the washing machine.",
      },
      {
        question: "Can you move at weekends?",
        answer: "Yes, we move every day of the week.",
      },
      {
        question: "Can you take away what isn't coming?",
        answer:
          "Yes, book a junk removal for the same day and we'll take it to recycling or reuse.",
      },
      {
        question: "Which parts of Täby do you cover?",
        answer:
          "All of Täby: Täby centrum, Tibble, Grindtorp, Näsbydal, Näsbypark, Roslags-Näsby, Viggbyholm, Gribbylund, Ensta, Skarpäng, Ella Gård, Täby kyrkby, Hägernäs, Arninge and Täby Park.",
      },
      {
        question: "Which other municipalities do you move in?",
        answer: `We provide moving help across the Stockholm region: ${stockholmKommunerText}. Driving within 20 km is always included.`,
      },
    ],
    priceHint: PACKAGE_PRICE_HINT_EN.transport,
    pricingRows: getPackagePricingRows("transport", "en"),
    dynamicHeadings: {
      threeSteps: "Moving help in Täby in 3 steps",
      step2Text:
        "We arrive with van and movers, clear room by room and load the heavy things furthest in. Then we drive to the new address — one or several.",
      step3Title: "All carried in",
      step3Desc:
        "We carry in and place the furniture in the right rooms, and reassemble what was taken apart. You can start unpacking straight away.",
      ctaHeading: "Book moving help in Täby today",
      ctaText:
        "Enter addresses, choose a package and a time. The price shows instantly and the RUT deduction comes off the invoice.",
    },
  },
};
